import { gql, useQuery } from '@apollo/client';
import { useEffect, useRef, useContext } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { LoadingContext } from '../../../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

const GET_HEADER_TEXT = gql`
query GetHeaderText {
  headers {
    id
    publishedAt
    title
    content
  }
}
`;
export default function HeaderText() {
    const { loading, error, data } = useQuery(GET_HEADER_TEXT);
    const { isBureauLoaded } = useContext(LoadingContext);
    const containerRef = useRef(null);
    const titleRef = useRef(null);
    const paragraphRef = useRef(null);
    const buttonsRef = useRef(null);

    // Add animations when text comes into view
    useGSAP(() => {
        if (!loading && data && data.headers.length > 0 && isBureauLoaded) {
            // Set initial state - invisible
            gsap.set([titleRef.current, paragraphRef.current, buttonsRef.current], {
                opacity: 0,
                y: 30
            });

            // Animate title
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "20% 100%",
                    toggleActions: "play none none reverse"
                }
            });

            // Animate paragraph with delay
            gsap.to(paragraphRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                delay: 0.2,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "20% 100%",
                    toggleActions: "play none none reverse"
                }
            });

            // Animate buttons with delay
            gsap.to(buttonsRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                delay: 0.4,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "20% 100%",
                    toggleActions: "play none none reverse"
                }
            });
        }
    }, [loading, data, isBureauLoaded]);

    // Check if data is still loading
    if (loading) {
      return <p>Loading...</p>;
    }
    // Check if there's an error
    if (error) {
      return <p>Error: {error.message}</p>;
    }
    return (
        <>
            {data.headers.map((headers, index) => 
                <div key={index} className='relative flex gap-6 max-w-[90%] md:max-w-[80%] mx-auto pt-[8rem]' ref={containerRef}>
                    <div className='flex flex-col justify-center items-center mt-5'>
                        <div className='w-5 h-5 rounded-full bg-[#915EFF]'></div>
                        <div className='violet_gradient w-1 sm:h-80 h-40'></div>
                    </div>
                    <div className='text-white-text mt-4 max-w-4xl'>
                        <h1 ref={titleRef} className='text-[2.5rem] md:text-[4.5rem]'>{headers.title}</h1>
                        <p ref={paragraphRef} className='max-w-[27rem] text-gray-text lg:text-[30px] sm:text-[26px] xs:text-[20px] text-[16px] lg:leading-[40px] mt-2'>{headers.content}</p>
                        <div ref={buttonsRef} className="flex gap-4 mt-4">
                            <button>
                                <a href="https://www.linkedin.com/in/mauro-coone-7415a425b/" target="_blank" aria-label="Header Link">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32.14 31.07" width="40" height="40">
                                        <defs>
                                            <style>{`.cls-1 { fill: #915eff; }`}</style>
                                        </defs>
                                        <path className="cls-1" d="M25.7,24.52h-4.75v-6.2c0-1.8-.22-2.66-1.49-2.66s-1.8.69-1.8,2.56v6.3h-4.75v-12.73h4.6v.83c.72-.62,1.79-1.12,3.16-1.12,4.43,0,5.03,3.08,5.03,6.09v6.93h0ZM21.91,23.59h2.83v-6c0-3.18-.68-5.16-4.07-5.16-1.67,0-2.74.89-3.16,1.66l-.14.25h-.82v-1.61h-2.67v10.86h2.83v-5.37c0-.96,0-3.5,2.76-3.5,2.45,0,2.45,2.34,2.45,3.59v5.27h0Z"/>
                                        <path className="cls-1" d="M9.12,11.11c-1.48,0-2.68-1.16-2.68-2.59s1.2-2.59,2.68-2.59,2.68,1.16,2.68,2.59-1.2,2.59-2.68,2.59ZM9.12,6.86c-.95,0-1.71.74-1.71,1.66s.77,1.66,1.71,1.66,1.71-.74,1.71-1.66-.77-1.66-1.71-1.66Z"/>
                                        <path className="cls-1" d="M11.5,24.52h-4.76v-12.73h4.76v12.73ZM7.71,23.59h2.83v-10.86h-2.83v10.86Z"/>
                                        <path className="cls-1" d="M16.07.93c8.33,0,15.11,6.55,15.11,14.6s-6.78,14.6-15.11,14.6S.96,23.59.96,15.53,7.74.93,16.07.93ZM16.07,0C7.19,0,0,6.95,0,15.53s7.19,15.53,16.07,15.53,16.07-6.95,16.07-15.53S24.94,0,16.07,0Z"/>
                                    </svg>
                                </a>
                            </button>

                            <button>
                                <a href="https://github.com/MauroCoone" target="_blank" aria-label="Link to some section">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32.14 31.07" width="40" height="40">
                                        <defs>
                                            <style>{`.cls-1 { fill: #915eff; }`}</style>
                                        </defs>
                                        <path className="cls-1" d="M15.86.93c8.59,0,15.31,6.48,15.31,14.74,0,3.29-.98,6.41-2.84,9.02-1.8,2.54-4.3,4.4-7.23,5.4,0-.21,0-.55,0-.97,0-1.11.01-2.79.01-4.25,0-.94-.17-1.68-.41-2.25,3.21-.48,6.75-1.74,6.75-7.83,0-1.88-.68-2.98-1.56-4,.23-.87.44-2.5-.35-4.48l-.17-.41-.44-.14c-.21-.07-.45-.1-.7-.1-1.43,0-3.37,1.14-4.25,1.71-1.25-.31-2.58-.47-3.89-.47s-2.64.16-3.89.47c-.88-.57-2.83-1.71-4.25-1.71-.25,0-.48.03-.69.1l-.44.14-.17.42c-.79,1.98-.57,3.63-.35,4.48-.9,1.08-1.42,2.16-1.42,4,0,6.11,3.46,7.37,6.6,7.84-.1.24-.18.49-.25.75-.28.09-.64.17-1.02.17-.85,0-1.51-.42-2.01-1.3-1.12-1.94-3.18-2.12-3.26-2.13h-.03s-.03,0-.03,0h-.06c-1.09,0-1.47.47-1.6.86-.27.82.5,1.49.99,1.82l.07.05.08.03c.47.22,1.05,1.21,1.3,1.82.59,1.73,2.27,2.69,4.73,2.69.25,0,.49,0,.7-.02,0,.66,0,1.35.01,1.92,0,.34,0,.63,0,.81C5.05,28.01.96,22.23.96,15.68c0-4.06,1.52-7.8,4.29-10.53C8,2.43,11.77.93,15.86.93ZM15.86,0C6.87,0,0,6.77,0,15.68c0,7.12,4.52,13.22,10.98,15.37.1.02.19.03.28.03.62,0,.84-.44.84-.8,0-.4-.02-2.6-.02-3.94,0,0-.73.16-1.66.16-1.4,0-3.25-.35-3.82-2.07,0,0-.74-1.87-1.8-2.35,0,0-1.46-.99.06-.99h.04s1.61.13,2.5,1.66c.79,1.38,1.88,1.77,2.85,1.77.77,0,1.46-.24,1.87-.43.15-1.03.57-1.74,1.04-2.17-3.62-.4-7.28-.92-7.28-7.1,0-1.77.49-2.65,1.53-3.78-.17-.42-.72-2.14.17-4.36.12-.04.25-.05.4-.05,1.48,0,4.07,1.79,4.07,1.79,1.3-.36,2.69-.55,4.07-.55s2.77.19,4.07.55c0,0,2.58-1.79,4.07-1.79.15,0,.28.02.4.06.89,2.23.34,3.94.17,4.36,1.04,1.14,1.67,2.02,1.67,3.78,0,6.2-3.82,6.69-7.44,7.1.6.51,1.1,1.47,1.1,2.98,0,2.17-.02,4.84-.02,5.37,0,.37.23.8.85.8.09,0,.18,0,.28-.03,6.48-2.13,10.87-8.23,10.87-15.35C32.14,6.77,24.85,0,15.86,0h0ZM5.75,21.57c-.07,0-.13.02-.15.07-.05.08.02.19.15.25.04.02.09.04.13.04.06,0,.12-.03.15-.08.04-.08-.02-.19-.15-.25-.04-.01-.09-.02-.12-.02ZM6.41,22.11s-.08.01-.11.04c-.08.06-.06.21.05.33.07.07.15.11.22.11.04,0,.08-.01.11-.04.08-.06.06-.21-.04-.33-.07-.07-.15-.11-.22-.11ZM7.07,22.94s-.08.01-.11.04c-.1.06-.1.23,0,.38.08.11.19.17.28.17.03,0,.06,0,.08-.02.1-.08.1-.25,0-.4-.06-.1-.16-.17-.26-.17ZM7.85,23.86c-.06,0-.12.02-.15.06-.1.08-.06.28.08.4.09.09.19.13.27.13.06,0,.11-.02.15-.06.08-.08.04-.28-.08-.4-.08-.09-.18-.13-.27-.13ZM9.03,24.57c-.13,0-.26.06-.29.16-.05.13.08.28.28.32.04.02.09.02.14.02.12,0,.24-.06.26-.15.04-.13-.08-.28-.28-.33-.04,0-.07-.01-.11-.01ZM11.65,24.62s-.04,0-.05,0c-.19.05-.32.17-.3.32.02.1.13.18.28.18.03,0,.07,0,.1-.01.19-.05.32-.17.3-.3-.02-.11-.16-.19-.33-.19h0ZM10.43,24.78s-.03,0-.05,0c-.19,0-.34.1-.34.23s.13.23.31.23c.02,0,.03,0,.05,0,.19,0,.34-.1.34-.23,0-.14-.14-.23-.32-.23Z"/>
                                    </svg>
                                </a>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}